<template>
  <div class="card" :class="cssClass">
    <div class="card-body">
      <div class="lp-savings-calculator">
        <div class="row g-lg-4">
          <div class="col-md-6">
            <div class="mb-3 mb-lg-4">
              <base-group-input :required-flag="true" inputLabel="Initial Deposit" front-group="$">
                <base-input label="Initial Deposit"
                            v-model.number="initialDeposit"
                            type="number"
                            placeholder="10000"
                            :show-label="false" />
              </base-group-input>
            </div>
            <div class="mb-3 mb-lg-4">
              <base-group-input inputLabel="Monthly Contribution"
                                front-group="$"
                                :required-flag="true">
                <base-input label="Monthly Contribution"
                            v-model.number="monthlyContribution"
                            type="number"
                            placeholder="1000"
                            :show-label="false" />
              </base-group-input>
            </div>
            <div class="mb-3 mb-lg-4">
              <label for="period-of" class="form-label">
                Over a period of
                <span>*</span>
              </label>
              <div class="input-group">
                <input type="number"
                       class="form-control"
                       name="period-of"
                       id="period-of"
                       v-model="periodDisplay" />
                <button class="btn btn-outline-secondary"
                        :class="{ active: isMonths }"
                        type="button"
                        @click.prevent="setIsMonths(true)">
                  Months
                </button>
                <button class="btn btn-outline-secondary"
                        :class="{ active: !isMonths }"
                        type="button"
                        @click.prevent="setIsMonths(false)">
                  Years
                </button>
              </div>
            </div>
            <div class="mb-3 mb-lg-4">
              <base-group-input :required-flag="true" inputLabel="Interest Rate" back-group="%">
                <base-input label="Interest Rate"
                            v-model.number="interestRate"
                            type="number"
                            placeholder="0.5"
                            :show-label="false" />
              </base-group-input>
            </div>
          </div>
          <div class="col-md-6">
            <div class="calculator__result-row">
              <span class="calculator-result-row__dot bg-transparent"></span>
              <p class="calculator-result-row__label">Interest Earned</p>
              <p class="calculator-result-row__number">
                <span><strong>{{ formatCurrency(interestEarned) }}</strong></span>
              </p>
            </div>
            <div class="calculator__result-row">
              <span class="calculator-result-row__dot bg-transparent"></span>
              <p class="calculator-result-row__label">Total Contributions</p>
              <p class="calculator-result-row__number">
                <span>
                  <strong>
                    {{
                    formatCurrency(totalContributionAmount)
                    }}
                  </strong>
                </span>
              </p>
            </div>
            <div class="calculator__result-row">
              <span class="calculator-result-row__dot bg-transparent"></span>
              <p class="calculator-result-row__label">Initial Deposit</p>
              <p class="calculator-result-row__number">
                <span>
                  <strong>{{ formatCurrency(initialDeposit) }}</strong>
                </span>
              </p>
            </div>
            <hr />
            <div class="calculator__total">
              <p>Your total savings</p>
              <div class="calculator-total__number">
                <span>$</span>
                <p>{{ total.toLocaleString() }}</p>
              </div>
            </div>
          </div>
        </div>
        <hr />
      </div>
      <slot name="cta"></slot>
    </div>
  </div>
  <div class="lp-calculator-disclaimer">
    <slot name="disclaimer"></slot>
  </div>
</template>

<script lang="ts">
  declare const DataIntelligenceSubmitScript: any;

  import { defineComponent, ref, computed, watch } from 'vue';

  import { formatCurrency } from '../helpers/calculators/formatting';
  import { debounce } from '../helpers/calculators/input';
  import { sendInsightReport } from '../helpers/calculators/insight';
  import {
    getFutureValueOfASeries,
    getPrincipleompoundInterest,
  } from '../helpers/calculators/financial';

  import BaseInput from './BaseInput.vue';
  import BaseGroupInput from './BaseGroupInput.vue';

  export default defineComponent({
    name: 'SavingsCalculator',
    components: {
      BaseInput,
      BaseGroupInput,
    },
    props: {
      cssClass: {
        type: String,
        required: false,
        default: '',
      },
    },
    setup() {
      const initialDeposit = ref(5000);
      const monthlyContribution = ref(100);
      const period = ref(60);
      const interestRate = ref(0.5);
      const compoundFrequency = ref(12);

      const isMonths = ref(true);
      const setIsMonths = (setToMonths: boolean) => {
        if (setToMonths) {
          isMonths.value = true;
        } else {
          isMonths.value = false;
        }
      };

      const periodDisplay = computed({
        get: () => {
          return isMonths.value ? period.value : period.value / 12;
        },
        set: (value) => {
          if (isMonths.value) {
            period.value = value;
          } else {
            period.value = value * 12;
          }
        },
      });

      const totalYears = computed(() => {
        return period.value / 12;
      });

      const totalContributionAmount = computed(() => {
        return monthlyContribution.value * (totalYears.value * 12);
      });

      const total = computed(() => {
        const principleCompoundInterest = getPrincipleompoundInterest(
          initialDeposit.value,
          interestRate.value,
          compoundFrequency.value,
          totalYears.value,
        );
        const contributionTotal = getFutureValueOfASeries(
          monthlyContribution.value,
          interestRate.value,
          compoundFrequency.value,
          totalYears.value,
        );
        const actualTotal = principleCompoundInterest + contributionTotal;

        if (isNaN(actualTotal)) {
          return initialDeposit.value + totalContributionAmount.value;
        }

        return Math.round(actualTotal);
      });

      const interestEarned = computed(() => {
        const earned =
          total.value - (initialDeposit.value + totalContributionAmount.value);
        return isNaN(earned) || earned < 0 ? 0 : earned;
      });

      // Sitefinity Insight
      const reportInputToDEC = () => {
        DataIntelligenceSubmitScript._client.sentenceClient.writeSentence({
          predicate: 'Input',
          object: 'Savings Calculator',
        });
      };
      watch(
        total,
        debounce(() => sendInsightReport(reportInputToDEC)),
      );

      return {
        interestEarned,
        interestRate,
        initialDeposit,
        isMonths,
        monthlyContribution,
        periodDisplay,
        total,
        totalContributionAmount,
        formatCurrency,
        setIsMonths,
      };
    },
  });
</script>

<style scoped></style>
